/**
 * Create Organization Modal
 *
 * Form for creating a new organization with scope template selection.
 */

import { useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { logAdminAction, ACTION_DESCRIPTIONS } from '@/lib/admin/auditLog';
import { sanitizeAndTrim, sanitizeEmail } from '@/lib/sanitize';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Building2 } from 'lucide-react';
import { OrgEntitlements } from '@/integrations/firebase/types';

interface CreateOrgModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (orgId: string) => void;
}

// Scope templates for quick setup
// Note: Empty enabledAhjs means ALL AHJs in the enabled states are accessible
const SCOPE_TEMPLATES = [
  {
    id: 'fl-hvac',
    name: 'Florida - HVAC',
    description: 'HVAC compliance for residential and commercial in Florida',
    config: {
      enabledStates: ['FL'],
      enabledAhjs: [], // Empty = all AHJs in FL
      projectTypes: ['residential', 'commercial'] as ('residential' | 'commercial')[],
      tradePacks: ['hvac'],
      rulePacksEnabled: ['hvac_manualj_pack', 'hvhz_noa_pack'],
      docTypesEnabled: ['permit_app', 'manual_j', 'spec_sheet', 'noa', 'pe_letter'],
    },
  },
  {
    id: 'fl-electrical',
    name: 'Florida - Electrical',
    description: 'Electrical compliance for residential and commercial in Florida',
    config: {
      enabledStates: ['FL'],
      enabledAhjs: [], // Empty = all AHJs in FL
      projectTypes: ['residential', 'commercial'] as ('residential' | 'commercial')[],
      tradePacks: ['electrical'],
      rulePacksEnabled: ['electrical_load_calc', 'nec_compliance'],
      docTypesEnabled: ['permit_app', 'electrical_drawings', 'load_calcs', 'spec_sheet'],
    },
  },
  {
    id: 'fl-plumbing',
    name: 'Florida - Plumbing',
    description: 'Plumbing compliance for residential and commercial in Florida',
    config: {
      enabledStates: ['FL'],
      enabledAhjs: [], // Empty = all AHJs in FL
      projectTypes: ['residential', 'commercial'] as ('residential' | 'commercial')[],
      tradePacks: ['plumbing'],
      rulePacksEnabled: ['plumbing_basic', 'fpc_compliance'],
      docTypesEnabled: ['permit_app', 'plumbing_drawings', 'spec_sheet'],
    },
  },
  {
    id: 'fl-general-contractor',
    name: 'Florida - General Contractor',
    description: 'Full compliance for all trades (HVAC, Electrical, Plumbing, Roofing)',
    config: {
      enabledStates: ['FL'],
      enabledAhjs: [], // Empty = all AHJs in FL
      projectTypes: ['residential', 'commercial'] as ('residential' | 'commercial')[],
      tradePacks: ['hvac', 'electrical', 'plumbing', 'roofing', 'general'],
      rulePacksEnabled: ['hvac_manualj_pack', 'hvhz_noa_pack', 'electrical_load_calc', 'plumbing_basic', 'commercial_energy'],
      docTypesEnabled: ['permit_app', 'manual_j', 'spec_sheet', 'noa', 'pe_letter', 'energy_calcs', 'structural_calcs'],
    },
  },
  {
    id: 'custom',
    name: 'Custom Configuration',
    description: 'Start with empty scope, configure manually after creation',
    config: {
      enabledStates: [],
      enabledAhjs: [],
      projectTypes: [] as ('residential' | 'commercial')[],
      tradePacks: [],
      rulePacksEnabled: [],
      docTypesEnabled: [],
    },
  },
];

// Default entitlements for new orgs
const DEFAULT_ENTITLEMENTS: Omit<OrgEntitlements, 'orgId' | 'updatedAt' | 'updatedBy'> = {
  planTier: 'core',
  seatsLimit: 3,
  checksLimitMonthly: 10,
  freeCheckCredits: 1,
  addons: {
    prioritySupport: false,
    extraExports: false,
  },
};

export function CreateOrgModal({ open, onOpenChange, onSuccess }: CreateOrgModalProps) {
  const { user } = useAuth();
  const { adminUser } = useAdminAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    ownerFirstName: '',
    ownerLastName: '',
    primaryContactEmail: '',
    notes: '',
    scopeTemplate: 'fl-hvac',
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.uid || !adminUser?.email || !adminUser?.adminRole) {
      toast({
        title: 'Error',
        description: 'Not authenticated as admin',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.name.trim() || !formData.primaryContactEmail.trim() ||
        !formData.ownerFirstName.trim() || !formData.ownerLastName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Organization name, owner name, and email are required',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      // SECURITY: Sanitize all user input before saving
      const sanitizedName = sanitizeAndTrim(formData.name, 200);
      const sanitizedFirstName = sanitizeAndTrim(formData.ownerFirstName, 50);
      const sanitizedLastName = sanitizeAndTrim(formData.ownerLastName, 50);
      const sanitizedEmail = sanitizeEmail(formData.primaryContactEmail);
      const sanitizedNotes = sanitizeAndTrim(formData.notes, 1000);

      // Generate org ID
      const orgId = `org_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Get scope template
      const template = SCOPE_TEMPLATES.find((t) => t.id === formData.scopeTemplate);
      const scopeConfig = template?.config || SCOPE_TEMPLATES[3].config;

      // Create org document
      await setDoc(doc(db, 'orgs', orgId), {
        name: sanitizedName,
        primaryContactEmail: sanitizedEmail,
        status: 'active',
        notes: sanitizedNotes || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Create entitlements document
      await setDoc(doc(db, 'org_entitlements', orgId), {
        orgId,
        ...DEFAULT_ENTITLEMENTS,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
      });

      // Create scope config document
      await setDoc(doc(db, 'org_scope_configs', orgId), {
        orgId,
        ...scopeConfig,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
      });

      // Log the action
      await logAdminAction({
        actorUserId: user.uid,
        actorEmail: adminUser.email,
        actorRole: adminUser.adminRole,
        actionType: 'CREATE_ORG',
        targetOrgId: orgId,
        description: ACTION_DESCRIPTIONS.CREATE_ORG(sanitizedName),
      });

      // Auto-invite the primary contact as owner
      try {
        const adminInviteUser = httpsCallable(functions, 'adminInviteUser');
        await adminInviteUser({
          orgId,
          email: sanitizedEmail,
          firstName: sanitizedFirstName,
          lastName: sanitizedLastName,
          role: 'owner',
        });

        toast({
          title: 'Organization Created',
          description: `${sanitizedName} has been created and invitation sent to ${sanitizedEmail}`,
        });
      } catch (inviteError: any) {
        console.error('Error inviting owner:', inviteError);
        // Org was created successfully, but invite failed
        toast({
          title: 'Organization Created',
          description: `${sanitizedName} created, but failed to send owner invitation. You can invite them manually.`,
          variant: 'default',
        });
      }

      // Reset form
      setFormData({
        name: '',
        ownerFirstName: '',
        ownerLastName: '',
        primaryContactEmail: '',
        notes: '',
        scopeTemplate: 'fl-hvac',
      });

      onSuccess(orgId);
    } catch (error: any) {
      console.error('Error creating organization:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create organization',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplate = SCOPE_TEMPLATES.find((t) => t.id === formData.scopeTemplate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[hsl(220,22%,12%)] border-[hsl(220,15%,20%)] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-[hsl(220,70%,60%)]" />
            Create Organization
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Set up a new customer organization with scope configuration
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Organization Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-gray-300">
              Organization Name *
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              placeholder="Acme HVAC Services"
              className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
            />
          </div>

          {/* Owner Name */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="ownerFirstName" className="text-gray-300">
                Owner First Name *
              </Label>
              <Input
                id="ownerFirstName"
                value={formData.ownerFirstName}
                onChange={(e) => handleChange('ownerFirstName', e.target.value)}
                required
                placeholder="John"
                className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ownerLastName" className="text-gray-300">
                Owner Last Name *
              </Label>
              <Input
                id="ownerLastName"
                value={formData.ownerLastName}
                onChange={(e) => handleChange('ownerLastName', e.target.value)}
                required
                placeholder="Smith"
                className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
              />
            </div>
          </div>

          {/* Owner Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-300">
              Owner Email *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.primaryContactEmail}
              onChange={(e) => handleChange('primaryContactEmail', e.target.value)}
              required
              placeholder="owner@acmehvac.com"
              className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
            />
            <p className="text-xs text-gray-500">An invitation will be sent to this email when the organization is created</p>
          </div>

          {/* Scope Template */}
          <div className="space-y-2">
            <Label className="text-gray-300">Scope Template *</Label>
            <Select
              value={formData.scopeTemplate}
              onValueChange={(v) => handleChange('scopeTemplate', v)}
            >
              <SelectTrigger className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
                {SCOPE_TEMPLATES.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTemplate && (
              <p className="text-xs text-gray-500">{selectedTemplate.description}</p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-gray-300">
              Internal Notes
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any internal notes about this organization..."
              rows={2}
              className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-[hsl(220,15%,25%)] bg-transparent text-gray-400 hover:bg-[hsl(220,15%,15%)] hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !adminUser}
              className="flex-1 bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)] text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Organization'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
